// src/pages/sherlocNarrativeCompletion.js
//
// The single Sherloc narrative-completion normalizer. Cortex and Quantum both read this
// file — it is twin-locked and must stay byte-identical between the two repos.
//
// Delivery availability and narrative completion are independent outcomes. A run can
// resolve a full campaign aggregate (KPI cards, platform performance) and still fail to
// produce narrative analysis. The backend states which happened in a completion contract;
// this module turns that contract into what an operator reads, so neither portal has to
// infer completion from run.status or invent its own key ladder.

export const SHERLOC_NARRATIVE_COMPLETION_STATES = Object.freeze({
  COMPLETED: "completed",
  PARTIAL: "partial",
  FAILED_VALIDATION: "failed_validation",
  FAILED_PROVIDER: "failed_provider",
  FAILED_NO_DELIVERY: "failed_no_delivery",
});

const KNOWN_STATES = new Set(Object.values(SHERLOC_NARRATIVE_COMPLETION_STATES));

const pickCompletionObject = (candidate) =>
  candidate && typeof candidate === "object" && !Array.isArray(candidate) ? candidate : null;

/**
 * Read the completion contract from anywhere the backend may carry it: the top-level
 * response field, the narrative-integrity envelope, or the persisted run row on reload.
 * Returns null when the response predates the contract — callers then fall back to the
 * older presence checks rather than inventing a state.
 */
export const normalizeSherlocNarrativeCompletion = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  const run = payload.run || payload.result?.run || payload.data?.run || payload.latest || null;

  const candidate =
    pickCompletionObject(payload.narrativeCompletion) ||
    pickCompletionObject(payload.narrative_completion) ||
    pickCompletionObject(payload.narrativeIntegrity?.completion) ||
    pickCompletionObject(payload.narrative_integrity?.completion) ||
    pickCompletionObject(run?.narrative_integrity?.completion) ||
    pickCompletionObject(run?.narrativeIntegrity?.completion) ||
    null;

  if (!candidate) return null;

  const state = String(candidate.state || "").trim();
  if (!KNOWN_STATES.has(state)) return null;

  return {
    state,
    reason: String(candidate.reason || "").trim() || null,
    detail: String(candidate.detail || "").trim() || null,
    hasDelivery: Boolean(candidate.hasDelivery),
    summaryPresent: Boolean(candidate.summaryPresent),
    crossPlatformNarrativePresent: Boolean(candidate.crossPlatformNarrativePresent),
    narrativeAnalysisCompleted: Boolean(candidate.narrativeAnalysisCompleted),
    providerFellBack: Boolean(candidate.providerFellBack),
    providerError: String(candidate.providerError || "").trim() || null,
    requestedProvider: candidate.requestedProvider || null,
    providerUsed: candidate.providerUsed || null,
    deliverySource: candidate.deliverySource || null,
    runStatus: candidate.runStatus || null,
  };
};

export const isSherlocNarrativeCompleted = (completion) =>
  Boolean(completion && completion.narrativeAnalysisCompleted);

/**
 * Operator-facing reason for an incomplete narrative. Never collapses every outcome into
 * "no narrative was returned" when the contract names something more precise, and never
 * repeats developer instructions (payload fields, normalizers) to an operator.
 */
export const resolveSherlocNarrativeIssueMessage = ({
  completion = null,
  hasDelivery = false,
  runErrorMessage = "",
} = {}) => {
  if (isSherlocNarrativeCompleted(completion)) return "";

  if (completion) {
    const { state, reason, providerError } = completion;

    if (state === SHERLOC_NARRATIVE_COMPLETION_STATES.FAILED_NO_DELIVERY) {
      return (
        completion.detail ||
        "No delivery could be resolved for this campaign and timeframe, so no narrative analysis was generated."
      );
    }

    if (state === SHERLOC_NARRATIVE_COMPLETION_STATES.FAILED_PROVIDER) {
      return providerError
        ? `The AI provider did not return narrative analysis for this run (${providerError}). Delivery and KPI cards remain available — retry Run Analysis.`
        : "The AI provider did not return narrative analysis for this run. Delivery and KPI cards remain available — retry Run Analysis.";
    }

    if (state === SHERLOC_NARRATIVE_COMPLETION_STATES.FAILED_VALIDATION) {
      if (reason === "missing_cross_platform_narrative") {
        return "The AI response did not include a cross-platform narrative, so this run was not accepted as a completed analysis. Delivery and KPI cards remain available — retry Run Analysis.";
      }
      if (reason === "missing_summary") {
        return "The AI response did not include campaign summary text, so this run was not accepted as a completed analysis. Delivery and KPI cards remain available — retry Run Analysis.";
      }
      return (
        completion.detail ||
        "The AI response failed narrative validation for this run. Delivery and KPI cards remain available — retry Run Analysis."
      );
    }

    if (state === SHERLOC_NARRATIVE_COMPLETION_STATES.PARTIAL) {
      if (reason === "route_canonical_repair") {
        return "Sherloc generated a grounded analysis from canonical delivery because the run did not return complete AI narrative output. KPI cards and platform performance are current.";
      }
      return "Sherloc generated a grounded analysis from canonical delivery because the AI provider was unavailable for this run. KPI cards and platform performance are current.";
    }
  }

  // Pre-contract responses: keep the previous behaviour rather than guessing a state.
  if (hasDelivery) {
    return "Sherloc found delivery for this timeframe, but the narrative analysis did not complete. KPI cards and platform performance are shown from available delivery.";
  }
  return String(runErrorMessage || "").trim();
};

/**
 * The Cross Platform Analysis block shown when a run resolved delivery but returned no
 * narrative. It states what happened and what is still trustworthy — it must never be
 * mistaken for a completed AI analysis, and must never print developer next-steps.
 */
export const buildSherlocNarrativeGapNotice = (completion = null) => {
  const lines = [
    `- ${resolveSherlocNarrativeIssueMessage({ completion, hasDelivery: true })}`,
    "- Platform performance below is derived from the locked campaign delivery snapshot, not from AI narrative analysis.",
    "- Run Analysis again to generate the cross-platform narrative.",
  ];
  return `CROSS PLATFORM ANALYSIS:\n${lines.join("\n")}`;
};
